'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getExperienceById, experiences } from '@/lib/data'
import BookNowCTA from '@/components/BookNowCTA'
import styles from '@/styles/experience-page.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import '@/components/Adventures.css'


export default function MountainServicesPage() {
  const thisExperience = getExperienceById('mountain-services')
  const t = useTranslations('experiences')
  const tCommon = useTranslations('common')
  const params = useParams()
  const locale = params.locale as string

  const relatedExperiences = experiences.filter(e => e.id !== 'mountain-services').slice(0, 2)

  const mainRef = useScrollAnimation<HTMLElement>({
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px',
  })

  if (!thisExperience) {
    return <div>{t('common.experienceNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/experiences/` },
        { name: thisExperience.title, href: `/${locale}/experiences/mountain-services/` },
      ]} />
      <Header />

      <HeroParallax
        backgroundImage={thisExperience.heroImage}
        title={thisExperience.title}
        subtitle={thisExperience.subtitle}
        location={thisExperience.location}
      />

      <main className={styles.pageWrapper} ref={mainRef}>
        <section className={styles.experienceDetails}>
          <div className="container">
            <div className={styles.detailsGrid}>
              <div className={styles.highlightsColumn} data-scroll-item>
                <h3>{t('mountainServices.highlightsTitle')}</h3>
                <p className={styles.duration}>{t('mountainServices.duration')}</p>

                <div className={styles.highlightItem} data-scroll-item style={{ transitionDelay: '120ms' }}>
                  <strong>{t('mountainServices.highlight1Title')}</strong>
                  <p>{t('mountainServices.highlight1')}</p>
                </div>

                <div className={styles.highlightItem} data-scroll-item style={{ transitionDelay: '200ms' }}>
                  <strong>{t('mountainServices.highlight2Title')}</strong>
                  <p>{t('mountainServices.highlight2')}</p>
                </div>

                <div className={styles.highlightItem} data-scroll-item style={{ transitionDelay: '280ms' }}>
                  <strong>{t('mountainServices.highlight3Title')}</strong>
                  <p>{t('mountainServices.highlight3')}</p>
                </div>
              </div>

              <div className={styles.featureColumn} data-scroll-item style={{ transitionDelay: '150ms' }}>
                <div className={styles.featureWrapper}>
                  <img
                    src="/images/experiences/mountain-services/mountain-services-feature.webp"
                    alt={t('mountainServices.alts.feature')}
                    className={styles.featureImage}
                    loading="lazy"
                  />
                </div>
              </div>

              <div className={styles.includedColumn} data-scroll-item style={{ transitionDelay: '300ms' }}>
                <h3>{t('common.whatsIncluded')}</h3>
                <ul>
                  <li>{t('mountainServices.included.item1')}</li>
                  <li>{t('mountainServices.included.item2')}</li>
                  <li>{t('mountainServices.included.item3')}</li>
                  <li>{t('mountainServices.included.item4')}</li>
                  <li>{t('mountainServices.included.item5')}</li>
                  <li>{t('mountainServices.included.item6')}</li>
                  <li>{t('mountainServices.included.item7')}</li>
                  <li>{t('mountainServices.included.item8')}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="inline"
            productId="mountain-services"
            productType="experience"
            productName={t('mountainServices.productName')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>

        <section className={styles.optionalSection}>
          <div className="container">
            <div className={styles.sectionHeader} data-scroll-item>
              <h2 className={styles.sectionTitle}>{t('mountainServices.optionalTitle')}</h2>
              <p className={styles.sectionDescription}>
                {t('mountainServices.optionalDescription')}
              </p>
            </div>

            <div className={styles.optionalGrid}>
              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '0ms' }}>
                <div className={styles.optionalIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12A10 10 0 1 1 12 2" />
                    <path d="M22 2L12 12" />
                    <path d="M16 2h6v6" />
                  </svg>
                </div>
                <h4>{t('mountainServices.optional.item1Title')}</h4>
                <p>{t('mountainServices.optional.item1')}</p>
              </div>

              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '100ms' }}>
                <div className={styles.optionalIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 8v4l3 3" />
                  </svg>
                </div>
                <h4>{t('mountainServices.optional.item2Title')}</h4>
                <p>{t('mountainServices.optional.item2')}</p>
              </div>

              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '200ms' }}>
                <div className={styles.optionalIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                </div>
                <h4>{t('mountainServices.optional.item3Title')}</h4>
                <p>{t('mountainServices.optional.item3')}</p>
              </div>

              <div className={styles.optionalCard} data-scroll-item style={{ transitionDelay: '300ms' }}>
                <div className={styles.optionalIcon}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </div>
                <h4>{t('mountainServices.optional.item4Title')}</h4>
                <p>{t('mountainServices.optional.item4')}</p>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.gallery}>
          <div className="container">
            <div className={styles.sectionHeader} data-scroll-item>
              <h2 className={styles.sectionTitle}>{t('mountainServices.galleryTitle')}</h2>
              <p className={styles.sectionDescription}>
                {t('mountainServices.galleryDescription')}
              </p>
            </div>

            <div className={styles.galleryGrid}>
              {[
                { src: '/images/experiences/mountain-services/mountain-services-gallery-01.webp', alt: t('mountainServices.alts.gallery01') },
                { src: '/images/experiences/mountain-services/mountain-services-gallery-02.webp', alt: t('mountainServices.alts.gallery02') },
                { src: '/images/experiences/mountain-services/mountain-services-gallery-03.webp', alt: t('mountainServices.alts.gallery03') },
                { src: '/images/experiences/mountain-services/mountain-services-gallery-04.webp', alt: t('mountainServices.alts.gallery04') },
                { src: '/images/experiences/mountain-services/mountain-services-gallery-05.webp', alt: t('mountainServices.alts.gallery05') },
                { src: '/images/experiences/mountain-services/mountain-services-gallery-06.webp', alt: t('mountainServices.alts.gallery06') },
              ].map((image, idx) => (
                <div key={idx} className={styles.galleryItem} data-scroll-item style={{ transitionDelay: `${idx * 80}ms` }}>
                  <img src={image.src} alt={image.alt} loading="lazy" />
                </div>
              ))}
            </div>

            <div className={styles.sectionCta} data-scroll-item>
              <a href={`/${locale}/experiences/`} className="btn-primary">
                {t('common.exploreExperiences')} &rarr;
              </a>
            </div>
          </div>
        </section>

        <section className={styles.relatedSection}>
          <div className="container">
            <h2 data-scroll-item>{t('common.relatedExperiences')}</h2>
            <div className={styles.relatedGrid}>
              {relatedExperiences.map((experience, i) => (
                <article key={experience.id} className={styles.relatedCard} data-scroll-item style={{ transitionDelay: `${120 + i * 120}ms` }}>
                  <div className={styles.relatedImage}>
                    <img src={experience.heroImage} alt={experience.title} loading="lazy" />
                  </div>
                  <div className={styles.relatedOverlay}>
                    <h3 className={styles.relatedTitle}>{experience.title}</h3>
                    <div className={styles.relatedContent}>
                      <p>{experience.subtitle}</p>
                      <a href={`/${locale}/experiences/${experience.id}/`} className={styles.relatedLink}>
                        {t('common.explore')} &rarr;
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <div data-scroll-item>
          <BookNowCTA
            variant="closing"
            productId="mountain-services"
            productType="experience"
            productName={t('mountainServices.productName')}
            bookingMode={thisExperience.bookingMode || 'whatsapp'}
          />
        </div>
      </main>

      <Footer />
    </>
  )
}
